import AttachmentMenuEntrySource from '../types/AttachmentMenuEntrySource';
import { buildReplyWithFilterableMenuItems } from '../utils/attachButtonMenuItemBuilder';
import { aFPCreateAttachmentHandler } from '../utils/createAttachmentHandler';
import focusToEditor from '../utils/focusToEditor';
import getEncryptionDropDownSubMenu from '../utils/getEncryptionDropDownSubMenu';
import getSensitivitySubMenu from '../utils/getSensitivitySubMenu';
import logAttachMenuData from '../utils/logAttachMenuData';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { ICommandBarStyleProps, ICommandBarStyles } from 'office-ui-fabric-react/lib/CommandBar';
import { IContextualMenuProps } from 'office-ui-fabric-react/lib/ContextualMenu';
import { Spinner } from 'office-ui-fabric-react/lib/Spinner';
import { IStyleFunctionOrObject } from 'office-ui-fabric-react/lib/Utilities';
import { logUsage, PerformanceDatapoint } from 'owa-analytics';
import { getMRUAttachmentStore } from 'owa-attachment-suggestions';
import { CreateAttachmentsProperties } from 'owa-attachment-well-data';
import { ControlIcons } from 'owa-control-icons';
import isEncryptionEnabled from 'owa-encryption-common/lib/utils/isEncryptionEnabled';
import { isFeatureEnabled } from 'owa-feature-flags';
import { DisplayOption, FilterableCommandBar, FilterableMenuItem } from 'owa-filterable-menu';
import { getStore as getCLPStore } from 'owa-mail-clp-store/lib/store/store';
import isCLPEnabled from 'owa-mail-clp-store/lib/utils/isCLPEnabled';
import clickAttachButton from 'owa-mail-compose-store/lib/actions/clickAttachButton';
import discardCompose from 'owa-mail-compose-store/lib/actions/discardCompose';
import floatCompose from 'owa-mail-compose-store/lib/actions/floatCompose';
import { openComposeWithViewState } from 'owa-mail-compose-store/lib/actions/openCompose';
import protectMessage from 'owa-mail-compose-store/lib/actions/protectMessage';
import setAsyncSendState from 'owa-mail-compose-store/lib/actions/setAsyncSendState';
import setImportance from 'owa-mail-compose-store/lib/actions/setImportance';
import setIsDirty from 'owa-mail-compose-store/lib/actions/setIsDirty';
import switchBodytype from 'owa-mail-compose-store/lib/actions/switchBodyType';
import toggleFrom from 'owa-mail-compose-store/lib/actions/toggleFrom';
import trySaveMessage from 'owa-mail-compose-store/lib/actions/trySaveMessage';
import { findComposePlugins } from 'owa-mail-compose-store/lib/plugins/ComposePlugins';
import { getStore } from 'owa-mail-compose-store/lib/store/composeStore';
import ComposeTarget from 'owa-mail-compose-store/lib/store/schema/ComposeTarget';
import { FloatingType } from 'owa-mail-compose-store/lib/store/schema/FloatingViewState';
import { isPublicFolderComposeViewState } from 'owa-mail-compose-store/lib/utils/createPublicFolderComposeViewState';
import handleOnSend from 'owa-mail-compose-store/lib/utils/handleOnSend';
import loadAttachmentSuggestions from 'owa-mail-compose-store/lib/utils/loadAttachmentSuggestions';
import shouldDisableEncryptionButton from 'owa-mail-compose-store/lib/utils/shouldDisableEncryptionButton';
import { MailIcons } from 'owa-mail-icons';
import { showMessageOptionsDialog } from 'owa-mail-message-options-view';
import ImportanceType from 'owa-service/lib/contract/ImportanceType';
import { isConsumer } from 'owa-session-store';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import isSmimeEnabledInViewState from 'owa-smime/lib/utils/isSmimeEnabledInViewState';
import strings from 'owa-strings/owa-mail-strings';
import { SurfaceActionIcons } from 'owa-surface-action-icons';
import isMessageOptionsEnabled from 'owa-surface-actions/lib/utils/isMessageOptionsEnabled';
import * as React from 'react';
import {
    getDefaultButtonsList,
    getSmimeAttachButtonsList,
} from '../utils/getAttachmentCommandItems';
import {
    AttachmentMultiMenuEntry,
    attachmentMultiMenuEntry,
    GetCreateAttachmentHandlerFunc,
} from 'owa-attachment-multi-menu-actions';
import {
    AttachmentSuggestions,
    ClientItem,
    mailStore,
    ReplyWithAttachmentSuggestion,
} from 'owa-mail-store';
import {
    AttachmentLocalComputerFilePicker,
    FilePickerAction,
    FileSelectionFilter,
} from 'owa-attachment-filepicker';
import ComposeViewState, {
    AsyncSendState,
} from 'owa-mail-compose-store/lib/store/schema/ComposeViewState';
import {
    ENCRYPT_GUID,
    ENCRYPT_DNF_GUID,
    IRM_DEFAULT_GUID,
} from 'owa-mail-compose-store/lib/store/schema/EncryptionGUID';

const styles = require('./ComposeCommandingBar.scss');
const classNames = require('classnames');

type AttachmentMultiMenuMap<T> = { [K in keyof AttachmentMultiMenuEntry]: T };

export interface ComposeCommandingBarProps {
    composeViewState: ComposeViewState;
    styles?: IStyleFunctionOrObject<ICommandBarStyleProps, ICommandBarStyles>;
    className?: string;
    isFloatingCompose?: boolean;
}

@observer
export default class ComposeCommandingBar extends React.Component<ComposeCommandingBarProps, {}> {
    private attachmentLocalComputerFilePicker: any;
    private attachmentLocalComputerToCloudFilePicker: any;

    constructor(props: ComposeCommandingBarProps) {
        super(props);
    }

    render() {
        return (
            <span>
                {this.createAttachmentLocalComputerFilePicker(
                    attachmentMultiMenuEntry.computer.getCreateAttachmentHandler,
                    ref => (this.attachmentLocalComputerFilePicker = ref)
                )}

                {this.createAttachmentLocalComputerFilePicker(
                    attachmentMultiMenuEntry.uploadAndShareFromCloud.getCreateAttachmentHandler,
                    ref => (this.attachmentLocalComputerToCloudFilePicker = ref)
                )}

                <FilterableCommandBar
                    styles={this.props.styles}
                    className={classNames(styles.filterableCommandBar, this.props.className)}
                    filterableItems={this.commandingBarItems}
                    elipisisAriaLabel={strings.composeEllipsisAriaLabelText}
                />
            </span>
        );
    }

    private createAttachmentLocalComputerFilePicker(
        getCreateAttachmentHandler: GetCreateAttachmentHandlerFunc,
        setRef: (ref: any) => void
    ) {
        return (
            <AttachmentLocalComputerFilePicker
                ref={setRef}
                fileSelectionFilter={FileSelectionFilter.None}
                action={FilePickerAction.Attach}
                createAttachmentHandler={getCreateAttachmentHandler(
                    this.props.composeViewState.attachmentWell,
                    (files, properties, dataProvider) => {
                        const composePlugins = findComposePlugins(this.props.composeViewState);
                        aFPCreateAttachmentHandler(
                            files,
                            composePlugins.inlineImagePlugin,
                            this.props.composeViewState,
                            properties,
                            dataProvider
                        );
                    }
                )}
            />
        );
    }

    private get commandingBarItems(): FilterableMenuItem[] {
        const { composeViewState } = this.props;
        if (composeViewState.asyncSendState == AsyncSendState.Sent) {
            return [this.closeMenuItem];
        } else if (this.props.composeViewState.isSending) {
            return [this.sendingMenuItem, this.minimizeMenuItem];
        } else {
            let barItems;

            if (isPublicFolderComposeViewState(composeViewState)) {
                barItems = [
                    this.sendMenuItem,
                    this.attachMenuItem,
                    this.protectMenuItem,
                    this.discardMenuItem,
                    this.showFromMenuItem,
                    this.setImportanceMenuItem,
                    this.switchBodyType,
                    this.openAccCheckerMenuItem,
                    this.openInPopoutMenuItem,
                ];
            } else {
                barItems = [
                    this.sendMenuItem,
                    this.attachMenuItem,
                    this.sensitivityMenuItem,
                    this.protectMenuItem,
                    this.discardMenuItem,
                    this.saveDraftMenuItem,
                    this.insertSignatureMenuItem,
                    this.showFromMenuItem,
                    this.protectMenuItemDropDown,
                    this.setImportanceMenuItem,
                    this.showMessageOptionsMenuItem,
                    this.switchBodyType,
                    this.openAccCheckerMenuItem,
                    this.openInPopoutMenuItem,
                ];
            }

            return barItems;
        }
    }

    @computed
    private get sendingMenuItem(): FilterableMenuItem {
        return new FilterableMenuItem(
            strings.sending,
            null,
            null,
            () => true /*shouldShow*/,
            DisplayOption.Left,
            false /*disabled*/,
            null,
            null,
            () => {
                return (
                    <div className={styles.sendSpinner}>
                        <Spinner />
                        <span className={styles.sendSpinnerText}>{strings.sending}</span>
                    </div>
                );
            }
        );
    }

    @computed
    private get minimizeMenuItem(): FilterableMenuItem {
        const { composeViewState } = this.props;

        return new FilterableMenuItem(
            null,
            MailIcons.ChromeMinimize,
            () => {
                floatCompose(composeViewState, FloatingType.Minimized);
            },
            () => {
                return (
                    this.props.isFloatingCompose &&
                    composeViewState.asyncSendState == AsyncSendState.Timeout
                );
            },
            DisplayOption.Right
        );
    }

    @computed
    private get closeMenuItem(): FilterableMenuItem {
        const { composeViewState } = this.props;

        return new FilterableMenuItem(
            null,
            ControlIcons.ChromeClose,
            () => {
                setAsyncSendState(composeViewState, AsyncSendState.Closed);
            },
            () => this.props.isFloatingCompose,
            DisplayOption.Right
        );
    }

    @computed
    private get sendMenuItem(): FilterableMenuItem {
        const { composeViewState } = this.props;
        let sendLabel: string;
        if (isPublicFolderComposeViewState(composeViewState)) {
            sendLabel = strings.composePostButton;
        } else {
            sendLabel = strings.composeSendButton;
        }

        return new FilterableMenuItem(
            sendLabel,
            ControlIcons.Send,
            () => {
                handleOnSend(composeViewState);
            },
            () => true /*shouldShow*/,
            DisplayOption.Left,
            composeViewState.sendDisabled /*disabled*/
        );
    }

    @computed
    private get attachMenuItem(): FilterableMenuItem {
        const composeViewState: ComposeViewState = this.props.composeViewState;
        const isSmimeCompose = isSmimeEnabledInViewState(composeViewState.smimeViewState);
        const attachmentMultiMenuEntriesClickHandler: AttachmentMultiMenuMap<() => void> = {
            computer: () => this.attachmentLocalComputerFilePicker.getInnerComponent().click(),
            onlineLocation: this.openFilePicker,
            uploadAndShareFromCloud: () =>
                this.attachmentLocalComputerToCloudFilePicker.getInnerComponent().click(),
        };

        const attachmentMenus = Object.keys(attachmentMultiMenuEntry).reduce(
            (agg, key: keyof AttachmentMultiMenuEntry) => {
                const attachmentMenuEntry = attachmentMultiMenuEntry[key];

                const aria = attachmentMenuEntry.ariaLabel && attachmentMenuEntry.ariaLabel();
                const menu = new FilterableMenuItem(attachmentMenuEntry.text(), null, () => {
                    attachmentMenuEntry.logUsage(AttachmentMenuEntrySource.CommandingBar);
                    attachmentMultiMenuEntriesClickHandler[key]();
                });
                menu.iconProps = attachmentMenuEntry.iconProps;
                menu.ariaLabel = aria;
                menu.title = aria;

                agg[key] = menu;

                return agg;
            },
            {} as AttachmentMultiMenuMap<FilterableMenuItem>
        );

        const menuItems = [attachmentMenus.computer];

        if (!isSmimeCompose) {
            menuItems.push(attachmentMenus.onlineLocation);

            if (attachmentMultiMenuEntry.uploadAndShareFromCloud.isEnabled()) {
                menuItems.push(attachmentMenus.uploadAndShareFromCloud);
            }

            // Attachment suggestions (replyWith + MRU)
            const referenceItem: ClientItem = mailStore.items.get(
                composeViewState.referenceItemId && composeViewState.referenceItemId.Id
            );
            const replyWithSuggestions: AttachmentSuggestions =
                referenceItem && referenceItem.AttachmentSuggestions;
            const mruSuggestions: ReplyWithAttachmentSuggestion[] = getMRUAttachmentStore()
                .attachments;

            let attachmentSuggestions: FilterableMenuItem[] = [];
            if (!!replyWithSuggestions || !!mruSuggestions) {
                attachmentSuggestions = buildReplyWithFilterableMenuItems(
                    composeViewState,
                    replyWithSuggestions,
                    mruSuggestions
                );
            }
            menuItems.push(...attachmentSuggestions);
        }

        const menu: IContextualMenuProps = {
            items: menuItems,
            onMenuOpened: () => {
                if (!isSmimeCompose) {
                    loadAttachmentSuggestions(composeViewState);
                }

                // Log that the Attach button itself was clicked.
                logUsage('AttachButtonClicked', [true /* isMail */]);

                // Log info about the buttons shown in the menu.
                logAttachMenuData(
                    composeViewState.referenceItemId,
                    isSmimeCompose ? getSmimeAttachButtonsList() : getDefaultButtonsList()
                );
            },
            onMenuDismissed: () => {
                // Log that the menu has been dismissed.
                logUsage('AttachButtonMenuClosed', [true /* isMail */]);
            },
        };

        // If both classic and cloudy attachments are disabled then we disable the button
        const userConfiguration = getUserConfiguration();
        const attachmentsDisabled =
            !userConfiguration.AttachmentPolicy.ClassicAttachmentsEnabled &&
            !userConfiguration.AttachmentPolicy.ReferenceAttachmentsEnabled;

        return new FilterableMenuItem(
            strings.attachTooltip,
            ControlIcons.Attach,
            null, // onClick
            () => true, // shouldShow
            null, // displayOption
            attachmentsDisabled, // disabled
            menu,
            null, // key
            null, // onRender
            null, // checked
            null, // ariaLabel
            null, // title
            null, // className
            null, // canCheck
            null // iconOnly
        );
    }

    private openFilePicker = () => {
        const { composeViewState } = this.props;
        const composePlugins = findComposePlugins(composeViewState);
        const inlineImagePlugin = composePlugins.inlineImagePlugin;

        // The inlineImagePlugin may be null if the editor has already been disposed
        if (inlineImagePlugin) {
            clickAttachButton(
                false,
                params => {
                    const baseProperties = { isInline: params.isInline, isHiddenInline: false };
                    const properties: CreateAttachmentsProperties = params.shouldShare
                        ? { ...baseProperties, shouldShare: true }
                        : { ...baseProperties, shouldShare: false };

                    aFPCreateAttachmentHandler(
                        params.files,
                        inlineImagePlugin,
                        composeViewState,
                        properties,
                        params.dataProviderType
                    );
                },
                composeViewState
            );
        }
    };

    @computed
    private get sensitivityMenuItem(): FilterableMenuItem {
        let { userLabels } = getCLPStore();

        let menu = getSensitivitySubMenu(userLabels, this.props.composeViewState);

        return new FilterableMenuItem(
            strings.sensitivityMenuText, // name
            ControlIcons.MarkAsProtected, // icon
            null, // onclick
            () => isCLPEnabled(), // shouldShow
            null, // displayOptions
            false, // disabled
            menu // menuContent
        );
    }

    @computed
    private get protectMenuItemDropDown(): FilterableMenuItem {
        const storeState = getStore();
        // This button is rendered as a drop down when CLP is enabled
        // and rendered as a button when CLP is not enabled
        const CLPEnabled = isCLPEnabled();
        return new FilterableMenuItem(
            strings.composeProtectButton,
            null,
            () => {
                if (!CLPEnabled) {
                    protectMessage(
                        this.props.composeViewState,
                        storeState.complianceConfigurations,
                        IRM_DEFAULT_GUID
                    );
                }
            },
            () => {
                return isEncryptionEnabled() && isFeatureEnabled('cmp-clp');
            },
            DisplayOption.Overflow,
            shouldDisableEncryptionButton(this.props.composeViewState),
            CLPEnabled
                ? {
                      items: [
                          getEncryptionDropDownSubMenu(ENCRYPT_GUID, this.props.composeViewState),
                          getEncryptionDropDownSubMenu(
                              ENCRYPT_DNF_GUID,
                              this.props.composeViewState
                          ),
                      ],
                  }
                : null
        );
    }

    @computed
    private get protectMenuItem(): FilterableMenuItem {
        const storeState = getStore();
        if (isConsumer()) {
            const encryptMenu = new FilterableMenuItem(
                strings.encryptMenuText,
                null /*icon*/,
                () => {
                    protectMessage(
                        this.props.composeViewState,
                        storeState.complianceConfigurations,
                        ENCRYPT_GUID
                    );
                },
                () => true,
                null /*displayOption*/,
                shouldDisableEncryptionButton(this.props.composeViewState, ENCRYPT_GUID)
            );

            const dnfMenu = new FilterableMenuItem(
                strings.encryptDisableForwardingMenuText,
                null /*icon*/,
                () => {
                    protectMessage(
                        this.props.composeViewState,
                        storeState.complianceConfigurations,
                        ENCRYPT_DNF_GUID
                    );
                },
                () => true,
                null /*displayOption*/,
                shouldDisableEncryptionButton(this.props.composeViewState, ENCRYPT_DNF_GUID)
            );

            return new FilterableMenuItem(
                strings.composeProtectButton,
                SurfaceActionIcons.Encryption,
                null /*onClick()*/,
                () => {
                    return isEncryptionEnabled();
                },
                null /*displayOption*/,
                shouldDisableEncryptionButton(this.props.composeViewState),
                [encryptMenu, dnfMenu]
            );
        } else {
            return new FilterableMenuItem(
                strings.composeProtectButton,
                isFeatureEnabled('cmp-clp') ? null : SurfaceActionIcons.Encryption,
                () => {
                    protectMessage(
                        this.props.composeViewState,
                        storeState.complianceConfigurations,
                        IRM_DEFAULT_GUID
                    );
                },
                () => {
                    return isEncryptionEnabled() && !isFeatureEnabled('cmp-clp');
                },
                isFeatureEnabled('cmp-clp') ? DisplayOption.Overflow : null,
                shouldDisableEncryptionButton(this.props.composeViewState)
            );
        }
    }

    @computed
    private get discardMenuItem(): FilterableMenuItem {
        return new FilterableMenuItem(strings.composeDiscardButton, ControlIcons.Delete, () => {
            discardCompose(this.props.composeViewState);
        });
    }

    @computed
    private get saveDraftMenuItem(): FilterableMenuItem {
        return new FilterableMenuItem(
            strings.saveDraft,
            null, // no icon
            () => {
                trySaveMessage(this.props.composeViewState);
                focusToEditor(this.props.composeViewState);
            },
            () => {
                return true;
            },
            DisplayOption.Overflow
        );
    }

    @computed
    private get openInPopoutMenuItem(): FilterableMenuItem {
        return new FilterableMenuItem(
            null, // no string
            ControlIcons.OpenInNewWindow,
            () => {
                openComposeWithViewState(this.props.composeViewState, ComposeTarget.Popout);
            },
            () => {
                return isFeatureEnabled('rp-popouts') && !this.props.composeViewState.isPopout;
            },
            DisplayOption.Right,
            null /* disabled */,
            null /* menuContent */,
            null /* key */,
            null /* onRender */,
            null /* checked */,
            strings.popoutsOpenInNewWindow,
            strings.popoutsOpenInNewWindow
        );
    }

    @computed
    private get insertSignatureMenuItem(): FilterableMenuItem {
        return new FilterableMenuItem(
            strings.insertSignature,
            null, // no icon
            () => {
                const datapoint = new PerformanceDatapoint('ComposeCommandInsertSignature');
                const composePlugins = findComposePlugins(this.props.composeViewState);
                composePlugins.contentOperationPlugin.insertSignature(
                    this.props.composeViewState.bodyType
                );
                setIsDirty(this.props.composeViewState);
                datapoint.end();
            },
            () => {
                return true;
            },
            DisplayOption.Overflow
        );
    }

    @computed
    private get showFromMenuItem(): FilterableMenuItem {
        return new FilterableMenuItem(
            !this.props.composeViewState.fromViewState.isFromShown
                ? strings.showFrom
                : strings.hideFrom,
            null, // no icon
            () => {
                toggleFrom(this.props.composeViewState);
                focusToEditor(this.props.composeViewState);
            },
            () => {
                return !this.props.composeViewState.fromViewState.isFromReadOnly;
            },
            DisplayOption.Overflow,
            isSmimeEnabledInViewState(this.props.composeViewState.smimeViewState)
        );
    }

    @computed
    private get setImportanceMenuItem(): FilterableMenuItem {
        return new FilterableMenuItem(
            strings.setImportance,
            null, // no icon
            null,
            () => {
                return true;
            },
            DisplayOption.Overflow,
            false, // disabled
            [
                this.getSetImportanceMenuSubItem(strings.highImportanceMenuText, 'High'),
                this.getSetImportanceMenuSubItem(strings.normalImportanceMenuText, 'Normal'),
                this.getSetImportanceMenuSubItem(strings.lowImportanceMenuText, 'Low'),
            ]
        );
    }

    private getSetImportanceMenuSubItem(
        menuText: string,
        importance: ImportanceType
    ): FilterableMenuItem {
        const { composeViewState } = this.props;

        return new FilterableMenuItem(
            menuText,
            null, // no icon
            () => {
                setImportance(composeViewState, importance);
                focusToEditor(composeViewState);
            },
            null, // shouldShow,
            null, // displayOption,
            null, // disabled,
            null, // menuContent
            null, // key,
            null, // onRender
            composeViewState.importance == importance, // checked
            null, // ariaLabel
            null, // title
            null, // className
            true // canCheck
        );
    }

    @computed
    private get showMessageOptionsMenuItem(): FilterableMenuItem {
        return new FilterableMenuItem(
            strings.showMessageOptions,
            null, // no icon
            () => {
                showMessageOptionsDialog(this.props.composeViewState);
            },
            () => {
                return isMessageOptionsEnabled();
            },
            DisplayOption.Overflow
        );
    }

    @computed
    private get switchBodyType(): FilterableMenuItem {
        const { composeViewState } = this.props;
        const isPlainText = composeViewState.bodyType != 'HTML';
        return new FilterableMenuItem(
            isPlainText ? strings.switchToHTMLMenuItem : strings.switchToPlainTextMenuItem,
            null, // no icon
            () => switchBodytype(composeViewState),
            () => true, // shouldShow
            DisplayOption.Overflow
        );
    }

    @computed
    private get openAccCheckerMenuItem(): FilterableMenuItem {
        const { composeViewState } = this.props;
        const isPlainText = composeViewState.bodyType != 'HTML';
        return new FilterableMenuItem(
            strings.accCheckerMenuItemText,
            null, // no icon
            this.openAccChecker,
            () => {
                return isFeatureEnabled('cmp-accChecker');
            },
            DisplayOption.Overflow,
            isPlainText, // isDisabled
            null, // menuContent
            null, // key
            null, // onRender
            composeViewState.accChecker.isOpen, // isChecked
            null, // ariaLabel
            null, // title
            null, // className
            true // canCheck
        );
    }

    private openAccChecker = () => {
        const { composeViewState } = this.props;
        const composePlugins = findComposePlugins(composeViewState);
        const accCheckerPlugin = composePlugins.accCheckerPlugin;

        // The accCheckerPlugin may be null if the editor has already been disposed
        if (accCheckerPlugin) {
            if (accCheckerPlugin.isImported() && composeViewState.accChecker.isOpen) {
                accCheckerPlugin.getPlugin().then(plugin => plugin.closeChecker());
            } else {
                accCheckerPlugin.addOnImportedCallback(plugin => plugin.openChecker());
                accCheckerPlugin.getPlugin();
            }
        }
    };
}
